import { fetchSubProductsOrNominals } from '@api/useQueryCalls';
import { Button } from '@components/primitives';
import useBPMutation from '@customHooks/useBPRequest';
import useFavourites from '@customHooks/useFavourites';
import { useToast } from '@hooks';
import RequestTransactionSheet from '@molecules/RequestTransactionSheet';
import { useNavigation } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import { getStore } from '@store/storeUtils';
import { showErrorOnAPI } from '@utils/utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import logPdamFailure from './analyticsPdam';
import styles from './styles';
import { disabledButtonForPDAM, refetchBPIfStale, shouldShowAccountInput } from '../BPUtils';
import { logPageBack, logPageLaunch, logRequestBP } from '../GGNominals/analyticsNominals';
import { scale } from '@utils/normalize';

export function getInitialSelectedAccNum(
  productConfig: { hasAccountInput?: boolean },
  mobileNumber: string,
): string | null {
  return productConfig.hasAccountInput ? null : mobileNumber;
}

export function onPressHandler(
  selectedAccountNumber: string | null,
  selectedBPAccount: any,
  productApiData: { prefix: string },
  selectedPDAMRegion: { code: string },
  validatedReferralCode: string,
  requestBP: any,
  setAccountInputError: (msg: string) => void,
  t: (key: string) => string,
  productConfig: { [key: string]: any },
  source: string,
  meAmplitudeContext: any[],
  isFavourite: boolean,
): () => void {
  return () => {
    const { amplitudeAccountName } = selectedBPAccount;
    if (selectedAccountNumber) {
      logRequestBP(
        selectedAccountNumber,
        productConfig,
        selectedPDAMRegion,
        source,
        validatedReferralCode,
        meAmplitudeContext,
        amplitudeAccountName,
        isFavourite,
      );
      const request = {
        accountNumber: selectedAccountNumber,
        employeeAccountNumber: selectedBPAccount.id,
        transCategoryPrefix: productApiData.prefix,
        productCode: selectedPDAMRegion.code,
        referralCode: validatedReferralCode,
        zoneId: null,
        month: null,
      };
      requestBP(request);
    } else {
      setAccountInputError(t('bp.selectOrInputAccountNumber'));
    }
  };
}

type GGPdamProps = {
  route: {
    params: {
      source: string;
      productConfig: {
        productId: number;
        noOfColumns: number;
        productName: string;
        hasAccountInput: boolean;
      };
      productApiData: {
        prefix: string;
      };
    };
  };
};

function GGPdam(props: GGPdamProps) {
  const { setLoading, setInitialData, selectedBPAccount, validatedReferralCode, loginData, meAmplitudeContext } =
    getStore();

  const { accountTypeId } = selectedBPAccount;
  const { route } = props;
  const { params } = route;
  const { productConfig, productApiData, source } = params;
  const { productId, productName } = productConfig;

  const [sAccountInputError, setAccountInputError] = useState('');
  const [regionsData, setRegionsData] = useState<any[]>([]);
  const { t } = useTranslation('common');
  const toast = useToast();
  const navigation = useNavigation();
  const [openRTS, setOpenRTS] = useState(false);
  const { mobileNumber } = loginData;
  const [selectedAccountNumber, setSelectedAccountNumber] = useState<string | null>(
    getInitialSelectedAccNum(productConfig, mobileNumber),
  );
  const [selectedPDAMRegion, setSelectedPDAMRegion] = useState<any>(null);
  const [bpReqData, setBpReqData] = useState<Record<string, any>>({});

  const { requestBP, isBPReqError, bpError, apiPayload } = useBPMutation({
    setOpenRTS,
    setBpReqData,
  });

  const { isLoading, isSuccess, data, isError, error, isStale, refetch } = fetchSubProductsOrNominals({
    productId,
    accountTypeId,
  });

  useEffect(() => {
    navigation.setOptions({ title: productName });
  }, [productName]);

  const isFavourite = useFavourites(
    navigation,
    productApiData?.id,
    productApiData?.isDefaultFavorites || productApiData?.isFavorites,
    source,
    productApiData?.prefix,
    productApiData?.canAddFavorites,
  );

  useEffect(() => {
    logPageLaunch(productConfig, source, validatedReferralCode, isFavourite);
    refetchBPIfStale(isStale, refetch);
    return () => logPageBack(productConfig, source, validatedReferralCode);
  }, []);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    showErrorOnAPI(isError, error, toast, t, navigation, setInitialData);
  }, [isError, error]);

  useEffect(() => {
    logPdamFailure(isBPReqError, selectedAccountNumber, productConfig, apiPayload, t, bpError, source);
  }, [isBPReqError, bpError]);

  useEffect(() => {
    if (isSuccess && data) {
      const regionKeys = Object.keys(data);
      const regData: any[] = [];
      regionKeys.forEach(rKey => {
        regData.push(data[rKey][0]);
      });
      setRegionsData(regData);
    }
  }, [isSuccess, data]);

  return (
    <View style={appStyles.safeAreaView}>
      <View style={appStyles.flex1}>
        {shouldShowAccountInput({
          selectedAccountNumber,
          setSelectedAccountNumber,
          setSelectedPDAMRegion,
          regionsData,
          sAccountInputError,
          setAccountInputError,
          ...params,
        })}
      </View>
      <View style={[appStyles.borderWithShadow, styles.bottomButton]}>
        <Button
          testID="btnConfirm"
          paddingY={scale(8)}
          isDisabled={disabledButtonForPDAM(selectedAccountNumber, selectedPDAMRegion)}
          onPress={onPressHandler(
            selectedAccountNumber,
            selectedBPAccount,
            productApiData,
            selectedPDAMRegion,
            validatedReferralCode,
            requestBP,
            setAccountInputError,
            t,
            productConfig,
            source,
            meAmplitudeContext,
            isFavourite,
          )}>
          {t('common.confirmed')}
        </Button>
      </View>
      <RequestTransactionSheet
        openRTS={openRTS}
        setOpenRTS={setOpenRTS}
        productConfig={productConfig}
        productApiData={productApiData}
        selectedAccountNumber={selectedAccountNumber}
        nominalSelected={selectedPDAMRegion}
        bpReqData={bpReqData}
      />
    </View>
  );
}

export default GGPdam;
